import { StarIcon } from "@heroicons/react/outline";
import moment from "moment";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArtifactType,
  AttendeeRole,
  AttendeeStatus,
  DashboardPeopleTableUserArtifactsFragmentFragment,
  DashboardPeopleTableUserMeetingFragmentFragment,
} from "types/graphql-schema";
import { BasicUser, TFLocationState } from "types/topicflow";

import ArtifactCreationDialog from "@apps/artifact-creation-dialog/artifact-creation-dialog";
import GoalProgress, {
  GoalProgressView,
} from "@apps/goal-progress/goal-progress";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import AppLink, { useLink } from "@components/link/link";
import { TableBodyCell, TableBodyRow } from "@components/table/table";
import TimeAgoCustom from "@components/time-ago/time-ago";
import Tooltip from "@components/tooltip/tooltip";
import {
  assertEdgesNonNull,
  assertNonNull,
  getUrl,
  getUrlUsingTypename,
  removeCurrentYear,
  toWithBackground,
} from "@helpers/helpers";

import getDashboardRecognitionsQuery from "../graphql/get-dashboard-recognitions-query";
import getPeopleTableMetadataQuery from "../graphql/get-people-table-metadata-query";
import { peopleTableGoalMax } from "./people-table";

const DashboardPeopleTableRow = ({
  selectedUser,
  user,
  userMetadata,
  loadingArtifacts,
  hideOneOnOneCTA = false,
}: {
  userMetadata?: DashboardPeopleTableUserArtifactsFragmentFragment & {
    meetings: DashboardPeopleTableUserMeetingFragmentFragment[];
  };
  selectedUser: BasicUser & { __typename: "UserNode" };
  user: BasicUser & { __typename: "UserNode" };
  loadingArtifacts: boolean;
  hideOneOnOneCTA?: boolean;
}) => {
  const location = useLocation<TFLocationState>();
  const label = useLabel();
  const link = useLink();
  const currentOrganization = currentOrganizationVar();
  const [
    isShowingCreateRecognitionDialog,
    setIsShowingCreateRecognitionDialog,
  ] = useState(false);
  const [isShowingCreateOneononeDialog, setIsShowingCreateOneononeDialog] =
    useState(false);

  const goals = userMetadata?.goals
    ? assertEdgesNonNull(userMetadata.goals)
    : [];
  const goalCount = userMetadata?.goals?.totalCount || goals.length;
  const maxGoalCountDisplayed =
    goalCount > peopleTableGoalMax ? peopleTableGoalMax - 1 : goalCount;
  const lastRecognition = userMetadata?.mostRecentlyReceivedRecognition;
  const nextMeeting = userMetadata?.meetings?.[0];

  const handleClickGiveRecognition = () => {
    setIsShowingCreateRecognitionDialog(true);
  };
  const handleCloseGiveRecognition = () => {
    setIsShowingCreateRecognitionDialog(false);
  };
  const handleClickScheduleMeeting = () => {
    setIsShowingCreateOneononeDialog(true);
  };
  const handleCloseScheduleMeeting = () => {
    setIsShowingCreateOneononeDialog(false);
  };

  const userTo = getUrlUsingTypename(user, location);

  const handleClick = (
    e: React.MouseEvent<HTMLTableRowElement | HTMLTableCellElement>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      (e.target instanceof HTMLDivElement &&
        e.target.classList.contains("js-clickable-row")) ||
      e.target instanceof HTMLTableCellElement
    ) {
      link.redirect(userTo);
      return;
    }
  };

  // RENDER
  return (
    <TableBodyRow
      key={user.id}
      onClick={handleClick}
      className="cursor-pointer hover:bg-gray-50"
    >
      <TableBodyCell className="text-gray-700" onClick={handleClick}>
        {isShowingCreateRecognitionDialog && (
          <ArtifactCreationDialog
            formOptions={{
              artifactType: ArtifactType.Recognition,
              recognitionRecipients: [user],
            }}
            onClose={handleCloseGiveRecognition}
            refetchQueries={[
              getDashboardRecognitionsQuery,
              getPeopleTableMetadataQuery,
            ]}
          />
        )}
        {isShowingCreateOneononeDialog && (
          <MeetingDialog
            formOptions={{
              title: `${label("oneonone")}: ${user.firstName} <> ${
                selectedUser.firstName
              }`,
              isFormalOneonone: true,
              attendees: [
                {
                  ...selectedUser,
                  email: assertNonNull(selectedUser.email),
                  role: AttendeeRole.Required,
                  participantStatus: AttendeeStatus.NotResponded,
                },
                {
                  ...user,
                  email: assertNonNull(user.email),
                  role: AttendeeRole.Required,
                  participantStatus: AttendeeStatus.NotResponded,
                },
              ],
              facilitatorId: selectedUser.id,
            }}
            onClose={handleCloseScheduleMeeting}
            refetchQueries={[getPeopleTableMetadataQuery]}
          />
        )}
        <div className="flex items-center gap-2 flex-auto min-w-0 js-clickable-row">
          <AppLink to={userTo}>
            <Avatar user={user} size="5" />
          </AppLink>
          <AppLink
            to={userTo}
            className="text-gray-900 text-sm hover:underline truncate"
          >
            {user.name}
          </AppLink>
          {userMetadata?.primaryManager?.id === user.id && (
            <Tooltip text="Primary Manager">
              <span>
                <StarIcon className="h-3 w-3 text-gray-500" />
              </span>
            </Tooltip>
          )}
        </div>
      </TableBodyCell>
      {currentOrganization.featureFlags.goals && (
        <TableBodyCell
          className="hidden @lg/people-table:table-cell"
          onClick={handleClick}
        >
          {loadingArtifacts ? (
            <div className="skeleton h-4 w-full rounded" />
          ) : (
            <div className="flex flex-none items-center gap-0.5 js-clickable-row">
              {goals.length === 0 && (
                <span className="text-gray-400">None</span>
              )}
              {goals
                .slice(0, maxGoalCountDisplayed)
                .map(
                  (goal) =>
                    goal.__typename === "GoalArtifactNode" && (
                      <GoalProgress
                        key={goal.id}
                        artifact={goal}
                        withPopover={true}
                        goalProgressView={GoalProgressView.pillOnly}
                      />
                    )
                )}
              {goalCount > maxGoalCountDisplayed && (
                <AppLink
                  to={`/goals/user/${user.id}`}
                  className="text-xs ml-1 hover:underline"
                >
                  +{goalCount - maxGoalCountDisplayed}
                </AppLink>
              )}
            </div>
          )}
        </TableBodyCell>
      )}
      <TableBodyCell onClick={handleClick}>
        <div className="js-clickable-row">
          {loadingArtifacts ? (
            <div className="skeleton h-4 w-full rounded" />
          ) : nextMeeting ? (
            <AppLink
              to={getUrl({
                meetingGroupId: nextMeeting.meetingGroupId,
                meetingId: nextMeeting.id,
              })}
              className="text-blue-link hover:underline"
            >
              {removeCurrentYear(
                moment(nextMeeting.startDatetime)
                  .format("MMM D, h:mma")
                  .replace(":00", "")
              )}
            </AppLink>
          ) : hideOneOnOneCTA ? null : (
            <button
              className="text-blue-link hover:underline text-left"
              onClick={handleClickScheduleMeeting}
            >
              Schedule {label("oneonone")}
            </button>
          )}
        </div>
      </TableBodyCell>
      {currentOrganization.featureFlags.recognitions && (
        <TableBodyCell
          className="hidden @2xl/people-table:table-cell"
          onClick={handleClick}
        >
          {loadingArtifacts ? (
            <div className="skeleton h-4 w-full rounded" />
          ) : (
            <div className="hidden @xl/people-table:flex flex-none items-center gap-0.5 tracking-tight js-clickable-row">
              {lastRecognition &&
              lastRecognition.__typename === "RecognitionArtifactNode" ? (
                <AppLink
                  to={toWithBackground({
                    pathname: getUrl({
                      artifactId: lastRecognition.id,
                      artifactType: lastRecognition.artifactType,
                    }),
                    location,
                  })}
                  className="hover:underline text-gray-800"
                >
                  <TimeAgoCustom
                    date={moment(lastRecognition.created).toDate()}
                  />
                </AppLink>
              ) : (
                <button
                  className="hover:underline text-gray-800"
                  onClick={handleClickGiveRecognition}
                >
                  Give a {label("recognition")}
                </button>
              )}
            </div>
          )}
        </TableBodyCell>
      )}
    </TableBodyRow>
  );
};

export default DashboardPeopleTableRow;
